import { exigerSession } from '../loaders.js';

/**
 * Le jeton du lien reçu par courriel arrive en FRAGMENT (`/archive#<jeton>`) :
 * un fragment ne part vers aucun serveur. On le garde le temps de l'onglet
 * (`sessionStorage`), pour qu'il survive au passage par `/connexion`, puis on
 * le retire de la barre d'adresse et de l'historique.
 *
 * React Router ne transmet pas le fragment aux chargeurs : on le lit sur
 * `window.location`, qui porte l'adresse réelle du navigateur.
 */
const CLE_JETON = 'artosera-archive-jeton';
const FORME_JETON = /^[A-Za-z0-9_-]{43}$/;

function poserJeton(jeton: string): boolean {
  try {
    sessionStorage.setItem(CLE_JETON, jeton);
    return true;
  } catch {
    return false;
  }
}

function lireJeton(): string | null {
  try {
    const jeton = sessionStorage.getItem(CLE_JETON);
    return jeton !== null && FORME_JETON.test(jeton) ? jeton : null;
  } catch {
    return null;
  }
}

/** Oublie le jeton une fois la réception accusée : il n'a plus d'usage. */
export function oublierJeton(): void {
  try {
    sessionStorage.removeItem(CLE_JETON);
  } catch {
    // Rien à faire.
  }
}

/** Met de côté le jeton du fragment, puis l'efface de l'adresse. */
function recueillirFragment(): string | null {
  const fragment = window.location.hash.replace(/^#/, '');
  if (!FORME_JETON.test(fragment)) return null;
  // Stockage indisponible (navigation privée stricte) : le jeton reste dans
  // l'adresse, la page fonctionne tant qu'on ne la quitte pas.
  if (poserJeton(fragment)) {
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}`,
    );
  }
  return fragment;
}

export interface DonneesArchive {
  /** Le jeton du lien de ce navigateur, s'il y en a un. */
  readonly jeton: string | null;
}

/** `/archive` : garde le jeton du fragment, puis exige la session. */
export async function archiveLoader(): Promise<DonneesArchive> {
  const recueilli = recueillirFragment();
  await exigerSession('/archive');
  return { jeton: lireJeton() ?? recueilli };
}
